import _ from '@lodash';
import { format } from 'date-fns';
import TableFilterListDto from 'modules/base/dto/table/TableFilterListDto';
import FuseLoading from '@fuse/core/FuseLoading/FuseLoading';
import { useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { showMessage } from 'app/store/fuse/messageSlice';
import { selectMainTheme } from 'app/store/fuse/settingsSlice';
import { ThemeProvider } from '@material-ui/core/styles';
import { Button, Dialog, DialogContent, DialogActions, DialogTitle, InputAdornment, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, TextField, Paper, Tooltip, IconButton } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import CompareArrowsIcon from '@material-ui/icons/CompareArrows';
import LinkIcon from '@material-ui/icons/Link';
import ListAltIcon from '@material-ui/icons/ListAlt';
import LoadIcon from '@material-ui/icons/SystemUpdateAltOutlined';
import BookmarkBorderIcon from '@material-ui/icons/BookmarkBorder';
import SearchIcon from '@material-ui/icons/Search';
import ConfirmDialog from 'modules/ui/component/ConfirmDialog';
import WhatIfScenarioEditDto from '../dto/WhatIfScenarioEditDto';
import WhatIfScenarioEditService from '../service/WhatIfScenarioEditService';
import WhatIfScenarioTableService from '../service/WhatIfScenarioTableService';
import { generateId } from './WhatIfUtil';
import { applyScenarioAttributesToExperiment, getScenarioChangedRows, getScenarioInputAttributes, getScenarioViewMessage } from './WhatIfScenarioUtil';
import { useWhatIf } from './WhatIfProvider';
import WhatIfScenarioSaveDialog from './WhatIfScenarioSaveDialog';
import WhatIfScenarioChangesDialog from './WhatIfScenarioChangesDialog';

const formatCreated = created => {
	if (!created) return '';

	try {
		return format(created, 'yyyy.MM.dd. HH:mm:ss');
	} catch (error) {
		return '';
	}
};

export default function WhatIfScenario() {
	const { id } = useParams();
	const { data, setExperiment, setViewMessage } = useWhatIf();
	const user = useSelector(({ auth }) => auth.user);
	const mainTheme = useSelector(selectMainTheme);
	const dispatch = useDispatch();
	const [sessionId, setSessionId] = useState(null);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [showListDialog, setShowListDialog] = useState(false);
	const [showSaveDialog, setShowSaveDialog] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
	const [showChangesDialog, setShowChangesDialog] = useState(null);
	const [scenarioList, setScenarioList] = useState([]);
	const [scenarioSearch, setScenarioSearch] = useState('');

	const whatIfId = Number.parseInt(id, 10);
	const currentExperiment = data ? data.experiment : null;
	const canUseScenario = id !== 'new' && Number.isFinite(whatIfId) && currentExperiment != null && _.isArray(currentExperiment.attributes);
	const canManageScenario = user?.role?.includes('expert') || user?.role?.includes('admin');
	const canSaveScenario = canManageScenario && canUseScenario && getScenarioInputAttributes(currentExperiment).length > 0;
	const filteredScenarioList = useMemo(() => {
		const searchValue = scenarioSearch.trim().toLowerCase();
		if (!searchValue) return scenarioList;

		return scenarioList.filter(scenario => [
			scenario.name,
			scenario.creator ? scenario.creator.name : null
		].filter(Boolean).join(' ').toLowerCase().includes(searchValue));
	}, [scenarioList, scenarioSearch]);

	useEffect(() => {
		if (!showListDialog) return;

		if (!canUseScenario) {
			setScenarioList([]);
			return;
		}

		setLoading(true);

		if (sessionId === null) {
			const newId = generateId();

			WhatIfScenarioTableService.getInstance(
				newId,
				true,
				new TableFilterListDto().setup({
					list: [
						{
							propertyName: 'whatIf',
							propertyType: 'Long',
							type: 'EQ',
							value: whatIfId
						}
					],
					activeCount: 1
				})
			).then(() => {
				setSessionId(newId);
			}).catch(() => {
				setLoading(false);
				dispatch(showMessage({ message: 'Failed to load scenarios' }));
			});
		} else {
			getList();
		}
	}, [showListDialog]);

	useEffect(() => {
		if (sessionId && showListDialog) getList();
	}, [sessionId]);

	const getList = () => {
		WhatIfScenarioTableService.getList(whatIfId, sessionId, 0, 0).then(result => {
			setScenarioList(result.data || []);
			setLoading(false);
		}).catch(() => {
			setLoading(false);
			dispatch(showMessage({ message: 'Failed to load scenarios' }));
		});
	};

	const handleSaveScenario = value => {
		if (!canSaveScenario) {
			dispatch(showMessage({ message: 'No input values available' }));
			return;
		}

		if (!value || !_.isArray(value.attributes) || value.attributes.length === 0) {
			dispatch(showMessage({ message: 'Select at least one input value' }));
			return;
		}

		const result = new WhatIfScenarioEditDto().setup({
			id: null,
			name: value.name,
			whatIf: whatIfId,
			attributes: value.attributes
		});

		setSaving(true);
		WhatIfScenarioEditService.save(result).then(response => {
			if (response.successful) {
				dispatch(showMessage({ message: 'Scenario saved successfully' }));
				setShowSaveDialog(false);
				if (sessionId) getList();
			} else {
				dispatch(showMessage({ message: 'Scenario save failed' }));
			}
		}).catch(() => {
			dispatch(showMessage({ message: 'Scenario save failed' }));
		}).finally(() => {
			setSaving(false);
		});
	};

	const handleDelete = () => {
		WhatIfScenarioTableService.deleteById(sessionId, showDeleteConfirm).then(() => {
			getList();
			setShowDeleteConfirm(null);
			setLoading(true);
		}).catch(() => {
			setShowDeleteConfirm(null);
			dispatch(showMessage({ message: 'Scenario delete failed' }));
		});
	};

	const loadScenario = item => {
		if (!canUseScenario) {
			dispatch(showMessage({ message: 'No experiment data available' }));
			return;
		}

		const experiment = applyScenarioAttributesToExperiment(currentExperiment, item.attributes);
		setExperiment(experiment);
		setViewMessage(getScenarioViewMessage(item));
		setShowListDialog(false);
		dispatch(showMessage({ message: 'Scenario loaded successfully' }));
	};

	const copyScenarioLink = async item => {
		const link = `${window.location.origin}/#/what-if/edit/${id}/S${item.id}`;

		try {
			await navigator.clipboard.writeText(link);
			dispatch(showMessage({ message: 'Scenario link copied to clipboard' }));
		} catch (error) {
			dispatch(showMessage({ message: 'Failed to copy scenario link' }));
		}
	};

	const showScenarioChanges = item => {
		setShowChangesDialog({
			scenario: item,
			rows: getScenarioChangedRows(currentExperiment, item.attributes)
		});
	};

	return (
		<>
			{canManageScenario && (
				<Button
					className="whitespace-nowrap mx-4"
					variant="contained"
					color="secondary"
					disabled={saving || !canSaveScenario}
					onClick={() => setShowSaveDialog(true)}
					startIcon={<BookmarkBorderIcon className="hidden sm:flex" />}
				>
					Save scenario
				</Button>
			)}

			<Button
				className="whitespace-nowrap mx-4"
				variant="contained"
				color="secondary"
				disabled={!canUseScenario}
				onClick={() => {
					setScenarioSearch('');
					setShowListDialog(true);
				}}
				startIcon={<ListAltIcon className="hidden sm:flex" />}
			>
				Scenarios
			</Button>

			{showSaveDialog && (
				<WhatIfScenarioSaveDialog
					defaultName={'Scenario - ' + format(new Date(), 'yyyy.MM.dd. HH:mm:ss').toString()}
					attributes={getScenarioInputAttributes(currentExperiment)}
					saving={saving}
					onCancel={() => setShowSaveDialog(false)}
					onSave={value => { handleSaveScenario(value); }}
				/>
			)}

			<ThemeProvider theme={mainTheme}>
				<Dialog open={showListDialog} maxWidth="lg" fullWidth>
					<DialogTitle disableTypography className="border-b flex flex-col px-0">
						<div className="flex items-center justify-between gap-32 px-32">
							<div className="whitespace-nowrap">What If Scenarios</div>
						</div>
					</DialogTitle>
					<DialogContent className="w-full flex flex-col p-20 overflow-hidden" style={{ maxHeight: 620 }}>
						{loading && <FuseLoading />}
						{!loading && (
							<>
								<TextField
									fullWidth
									label="Search Scenarios"
									variant="outlined"
									value={scenarioSearch}
									onChange={event => setScenarioSearch(event.target.value)}
									className="mb-16"
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<SearchIcon fontSize="small" />
											</InputAdornment>
										)
									}}
								/>
								<TableContainer component={Paper} className="flex-1">
								<Table className="w-full" aria-label="simple table">
									<TableHead>
										<TableRow>
											<TableCell>Name</TableCell>
											<TableCell>Creator</TableCell>
											<TableCell>Created</TableCell>
											<TableCell align="right">Actions</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{filteredScenarioList.length > 0 ? (
											filteredScenarioList.map(row => (
												<TableRow key={row.id}>
													<TableCell component="th" scope="row">
														{row.name}
													</TableCell>
													<TableCell>{row.creator ? row.creator.name : ''}</TableCell>
													<TableCell>{formatCreated(row.created)}</TableCell>
													<TableCell className="font-mono" align="right">
														<Tooltip title="Copy Link for Scenario">
															<span>
																<IconButton aria-label="copy-link" onClick={() => copyScenarioLink(row)}>
																	<LinkIcon fontSize="small" />
																</IconButton>
															</span>
														</Tooltip>
														<Tooltip title="Show changed values">
															<span>
																<IconButton aria-label="show-changes" onClick={() => showScenarioChanges(row)}>
																	<CompareArrowsIcon fontSize="small" />
																</IconButton>
															</span>
														</Tooltip>
														<Tooltip title="Load scenario">
															<span>
																<IconButton aria-label="load" onClick={() => loadScenario(row)}>
																	<LoadIcon fontSize="small" />
																</IconButton>
															</span>
														</Tooltip>
														{canManageScenario && (
															<Tooltip title="Delete">
																<span>
																	<IconButton
																		aria-label="delete"
																		onClick={event => {
																			setShowDeleteConfirm(row.id);
																			event.preventDefault();
																		}}
																	>
																		<DeleteIcon fontSize="small" />
																	</IconButton>
																</span>
															</Tooltip>
														)}
													</TableCell>
												</TableRow>
											))
										) : (
											<TableRow>
												<TableCell component="th" scope="row" colSpan={4} align="center">
													{scenarioList.length > 0 ? 'No matching scenarios' : 'No scenarios available'}
												</TableCell>
											</TableRow>
										)}
										<ConfirmDialog
											show={showDeleteConfirm}
											title="Delete"
											description="Do you want to delete this scenario?"
											onYes={() => handleDelete()}
											onClose={() => setShowDeleteConfirm(null)}
										/>
									</TableBody>
								</Table>
								</TableContainer>
							</>
						)}
					</DialogContent>
					<DialogActions className="justify-between border-t p-20">
						<Button onClick={() => setShowListDialog(false)} disabled={loading}>
							Close
						</Button>
						{canManageScenario && (
							<Button onClick={() => setShowSaveDialog(true)} disabled={loading || saving || !canSaveScenario}>
								Save new scenario
							</Button>
						)}
					</DialogActions>
				</Dialog>
			</ThemeProvider>

			{showChangesDialog && (
				<WhatIfScenarioChangesDialog
					scenario={showChangesDialog.scenario}
					rows={showChangesDialog.rows}
					onClose={() => setShowChangesDialog(null)}
				/>
			)}
		</>
	);
}
